import { useEffect, useState } from 'react';
import type { CategoryFormData, GuideCategory } from '../../types/guides';
import { Icon, Modal } from '../atoms';
import { FormField, ModalFooter, ModalHeader } from '../molecules';
import Button from '../atoms/Button';
import IconButton from '../atoms/IconButton';

interface CategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CategoryFormData) => void;
  onDelete?: (categoryId: string) => void;
  category?: GuideCategory | null;
}

export function CategoryModal({ 
  isOpen, 
  onClose, 
  onSave, 
  onDelete, 
  category 
}: CategoryModalProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [formData, setFormData] = useState<CategoryFormData>({
    name: '',
    displayName: '',
    description: '',
  });

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name,
        displayName: category.displayName,
        description: category.description ?? '',
      });
    } else {
      setFormData({
        name: '',
        displayName: '',
        description: '',
      });
    }
  }, [category, isOpen]);

  const handleClose = () => {
    setShowDeleteConfirm(false);
    onClose();
  };

  const handleSave = () => {
    if (!formData.name.trim() || !formData.displayName.trim()) {
      return;
    }
    onSave(formData);
    handleClose();
  };

  const handleDelete = () => {
    if (category && onDelete) {
      if (showDeleteConfirm) {
        onDelete(category.id);
        onClose();
        setShowDeleteConfirm(false);
      } else {
        setShowDeleteConfirm(true);
      }
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      size="md"
      closeOnEscape
      closeOnBackdrop
    >
      <ModalHeader
        title={category ? 'Edit Category' : 'Create New Category'}
        subtitle="Organize your guides with categories"
        onClose={onClose}
        actions={
          category && onDelete ? (
            <IconButton
              onClick={handleDelete}
              variant={showDeleteConfirm ? "neutral" : "ghost"}
              size="medium"
              title={showDeleteConfirm ? "Click again to confirm delete" : "Delete category"}
              aria-label={showDeleteConfirm ? "Confirm delete" : "Delete category"}
              icon={<Icon name="delete" size="sm" />}
            />
          ) : undefined
        }
      />

      {/* Form Content */}
      <div className="p-6 space-y-4">
        <div className="space-y-4">
          <FormField
            id="category-name"
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Enter category name (e.g. getting-started)"
            required
            hint="Used for URLs and technical references"
          />
          
          <FormField
            id="category-display-name"
            label="Display Name"
            value={formData.displayName}
            onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
            placeholder="Enter display name (e.g. Getting Started)"
            required
            hint="Human-friendly name shown in the interface"
          />
          
          <FormField
            id="category-description"
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Brief description of this category"
            as="textarea"
            autoResize
            maxHeight={120}
          />
        </div>
      </div>

      <ModalFooter>
        <div className="text-sm text-text-muted">
          {category && `Created ${category.createdAt.toLocaleDateString()}`}
        </div>
        <div className="flex items-center space-x-3">
          <Button
            onClick={onClose}
            variant="neutral"
            size="medium"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="primary"
            size="medium"
          >
            {category ? 'Update Category' : 'Create Category'}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
}